import React, { useState, useEffect } from "react";

import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { BlogArticleTile } from "@project-stories/blog/BlogArticleTile/BlogArticleTile";
import { Button } from "@project-stories/elements/Button/Button";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { ProductListPagination } from "@project-stories/ecommerce/Product/ProductListPagination";

const GridLayout = ({ initialArticles, settings }) => {
  const { section, blocks, globals } = settings;

  const articleBlocks = blocks?.filter((block) => block.type === "article_block");
  const initialPerPage = globals.articles_per_page || 3;
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(initialPerPage);
  const [articles, setArticles] = useState(articleBlocks || initialArticles|| []);
  const [currentArticles, setCurrentArticles] = useState(
    articleBlocks?.slice(0, articlesPerPage) || initialArticles?.slice(0, articlesPerPage) || [],
  );

  // Calculate total pages for pagination
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  const contentBlock = blocks?.find((block) => block.type === "content_block");
  
  const showPagination = section.show_pagination !== false;
  const taglineText = contentBlock?.settings?.tagline;
  const titleText = contentBlock?.settings?.title;
  const taglineType = contentBlock?.settings?.tagline_type || "h6";
  const titleType = contentBlock?.settings?.title_type || "h2";
  const showMetadata = section.show_metadata !== false;
  const imageRounding = section.image_rounding || "none";
  const showArticleDate = section.show_article_date || false;
  const showReadMoreButton = section.show_read_more_button || false;

  useEffect(() => {
    const handleArticlesComplete = (event) => {
      setCurrentPage(1);
      setArticles(event.detail.articles);
    };

    const handleNextPage = (event) => {
      if (event.detail.type === "pagination") {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("blog-articles-complete", handleArticlesComplete);
    window.addEventListener("nextpage", handleNextPage);

    return () => {
      window.removeEventListener(
        "blog-articles-complete",
        handleArticlesComplete,
      );
      window.removeEventListener("nextpage", handleNextPage);
    };
  }, []);

  useEffect(() => {
    const indexOfFirstArticle = (currentPage - 1) * articlesPerPage;
    const indexOfLastArticle = currentPage * articlesPerPage;
    const newArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
    setCurrentArticles(newArticles);
  }, [currentPage, articles, articlesPerPage]);

  // Pagination handlers
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      // Scroll to top of articles section
      const articlesSection = document.querySelector('.blog-articles');
      if (articlesSection) {
        articlesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const contentBlockRender = () => {
    if (taglineText || titleText || settings?.section?.subtext) {
      return (
        <div className="flex flex-col space-y-4 mb-8 text-center">
          {taglineText && (
            <Paragraph
              text={taglineText}
              type={taglineType}
              classes=""
            />
          )}
          {titleText && (
            <Headings
              text={titleText}
              type={titleType}
              classes="font-bold"
            />
          )}
          {settings?.section?.subtext && (
            <Paragraph
              text={settings?.section?.subtext}
              type={settings?.section?.subtext_type}
            />
          )}
        </div>
      );
    }
    return null;
  };

  const isReverse = isMobile
    ? section.layout_type_mobile === 'grid_reverse'
    : section.layout_type === 'grid_reverse';

  // Calculate responsive grid columns
  const getGridColumns = () => {
    if (section.columns_count_enable_responsive) {
      const mobileColumns = section.columns_count_mobile || 1;
      const tabletColumns = section.columns_count_tablet || 2;
      const desktopColumns = section.columns_count || 3;
      const ultrawideColumns = section.columns_count_ultrawide || 4;
      
      return `grid-cols-${mobileColumns} md:grid-cols-${tabletColumns} lg:grid-cols-${desktopColumns} xl:grid-cols-${ultrawideColumns}`;
    } else {
      // Fallback to existing behavior
      const columns = section.columns_count || 3;
      return `grid-cols-1 lg:grid-cols-${columns}`;
    }
  };

  return (
    <div className={`w-full color-${section.color_schema}`}>
      <div className={"flex flex-col items-center"}>
        <div className={"section-toprow__details"}>
          {contentBlockRender()}
        </div>
        <div className={"flex-item flex"}>
          {contentBlock?.settings.cta_text && (
            <Button
              link={contentBlock?.settings.cta_url || "/blogs"}
              label={contentBlock?.settings.cta_text || "Read All Articles"}
              style={contentBlock?.settings.cta_style || "primary"}
            />
          )}
        </div>
      </div>

      <div className="blog-articles flex flex-col">
        {currentArticles.length > 0 && (
          <>
            <div className={`article-items gap-[8px] lg:gap-[20px]`}>
              <div 
                className={`grid ${getGridColumns()}`}
                style={{
                  columnGap: settings?.section?.column_gap_enable_responsive 
                    ? `${isMobile ? (settings?.section?.column_gap_mobile || 16) : (settings?.section?.column_gap || 24)}px`
                    : `${settings?.section?.column_gap || 24}px`,
                  rowGap: settings?.section?.row_gap_enable_responsive
                    ? `${isMobile ? (settings?.section?.row_gap_mobile || 16) : (settings?.section?.row_gap || 24)}px`
                    : `${settings?.section?.row_gap || 24}px`
                }}
              >
                {currentArticles.map((article, index) => {
                  return (
                    <div
                      className="blog-articles__article article"
                      key={index}
                    >
                      <BlogArticleTile
                        article={article.article || article}
                        show_excerpt={true}
                        settings={settings}
                        showContent={false}
                        showCategory={true}
                        showMetadata={true}
                        imageRounding={imageRounding}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            {showPagination && totalPages > 1 && (
              <div className="flex justify-center items-center pt-16 lg:pt-20">
                {/* Previous Button */}
                {currentPage > 1 && (
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    className="flex items-center px-4 py-2 text-current hover:opacity-70 transition-opacity"
                    aria-label="Previous page"
                  >
                    <i className="icon icon-arrow-back mr-2"></i>
                    Previous
                  </button>
                )}
                
                <ProductListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loading={false}
                  goToPage={goToPage}
                  loadMoreProducts={() => {}} // Not used for page-based pagination
                  paginationType="pages"
                  paginationTextClass="text-current"
                />
                
                {/* Next Button */}
                {currentPage < totalPages && (
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    className="flex items-center px-4 py-2 text-current hover:opacity-70 transition-opacity"
                    aria-label="Next page"
                  >
                    Next
                    <i className="icon icon-arrow-forward ml-2"></i>
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {currentArticles.length <= 0 && (
          <div className={"blog-articles-empty"}>
            <Paragraph
              text={"There are no articles found with that filter."}
              type={"b2"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GridLayout;
